'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { GuideTocItem } from '../lib/guide-article';

type GuideDetailTocProps = {
  items: GuideTocItem[];
  variant?: 'desktop' | 'mobile' | 'both';
};

export default function GuideDetailToc({ items, variant = 'both' }: GuideDetailTocProps) {
  const rootLevel = Math.min(...items.map(item => item.level));
  const [activeId, setActiveId] = useState(items[0]?.id ?? '');
  const [isOpen, setIsOpen] = useState(false);
  const linkRefs = useRef(new Map<string, HTMLAnchorElement>());
  const mobileLinkRefs = useRef(new Map<string, HTMLAnchorElement>());
  const mobileNavRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const nav = mobileNavRef.current;
    const link = mobileLinkRefs.current.get(activeId);
    if (!nav || !link) return;
    // Scroll only the drawer list, keeping the article at its reading position.
    nav.scrollTop += link.getBoundingClientRect().top - nav.getBoundingClientRect().top
      - (nav.clientHeight - link.offsetHeight) / 2;
  }, [isOpen, activeId, items]);

  useEffect(() => {
    if (items.length === 0) return;

    let frame = 0;
    const updateActiveHeading = () => {
      frame = 0;
      const headingOffset = window.innerWidth <= 680 ? 64 : 80;
      let nextId = items[0].id;

      for (const item of items) {
        const heading = document.getElementById(item.id);
        if (!heading) continue;
        if (heading.getBoundingClientRect().top <= headingOffset) {
          nextId = item.id;
        } else {
          break;
        }
      }

      setActiveId((current) => (current === nextId ? current : nextId));
    };

    const scheduleUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(updateActiveHeading);
    };

    updateActiveHeading();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [items]);

  useEffect(() => {
    if (!activeId || window.innerWidth <= 680) return;
    linkRefs.current.get(activeId)?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    });
  }, [activeId]);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    const closeOnDesktop = () => {
      if (window.innerWidth > 680) setIsOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', closeOnDesktop);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', closeOnDesktop);
    };
  }, [isOpen]);

  return <>
    {variant !== 'mobile' && items.length > 0 && <nav className="guide-detail-side-card guide-detail-toc" aria-label="帖子目录">
      <div className="guide-detail-side-heading">
        <h2>帖子目录</h2>
      </div>
      <div className="guide-detail-toc-links">
        {items.map((item) => {
          const isActive = item.id === activeId;
          return (
            <a
              className={`${item.level === 3 ? 'is-subsection' : ''}${isActive ? ' is-active' : ''}`.trim()}
              href={`#${item.id}`}
              aria-current={isActive ? 'location' : undefined}
              key={item.id}
              ref={(element) => {
                if (element) linkRefs.current.set(item.id, element);
                else linkRefs.current.delete(item.id);
              }}
              onClick={() => setActiveId(item.id)}
            >
              {item.title}
            </a>
          );
        })}
      </div>
    </nav>}
    {variant !== 'desktop' && items.length > 0 && <><button type="button" className="hero-comment-entry guide-mobile-toc-button" aria-label="打开帖子目录" aria-expanded={isOpen} aria-controls="guide-mobile-toc-panel" onClick={() => setIsOpen(true)}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
        <path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" />
      </svg>
    </button>
    <div className={`guide-mobile-toc-overlay${isOpen ? ' is-open' : ''}`} aria-hidden={!isOpen} inert={!isOpen}>
      <button type="button" className="guide-mobile-toc-backdrop" aria-label="关闭目录" tabIndex={isOpen ? 0 : -1} onClick={() => setIsOpen(false)} />
      <section id="guide-mobile-toc-panel" className="guide-mobile-toc-panel" role="dialog" aria-modal={isOpen || undefined} aria-label="帖子目录">
        <header><h2>帖子目录</h2><button type="button" aria-label="关闭帖子目录" onClick={() => setIsOpen(false)}>×</button></header>
        <nav aria-label="目录条目" ref={mobileNavRef}>
          {items.length === 0 ? <p className="guide-mobile-toc-empty">暂无目录</p> : items.map(item => (
            <a key={item.id} data-depth={item.level - rootLevel} href={`#${item.id}`} ref={element => {
              if (element) mobileLinkRefs.current.set(item.id, element);
              else mobileLinkRefs.current.delete(item.id);
            }} className={`${item.level === 3 ? 'is-subsection' : ''}${item.id === activeId ? ' is-active' : ''}`.trim()} aria-current={item.id === activeId ? 'location' : undefined} onClick={event => {
              event.preventDefault();
              setActiveId(item.id);
              setIsOpen(false);
              const heading = document.getElementById(item.id);
              if (heading) {
                window.history.replaceState(window.history.state, '', `#${item.id}`);
                window.scrollTo({ top: window.scrollY + heading.getBoundingClientRect().top - 64, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
              }
            }}>{item.title}</a>
          ))}
        </nav>
      </section>
    </div></>}
  </>;
}
